import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { getBookingDetailsByVehicleId } from "../services/bookingService";
import { getAllVehicles } from "../services/vehicleService";
import bookingWS from "../services/bookingWebSocket";
import DriverLayout from "../components/DriverLayout";
import { toast } from "react-toastify";
import { 
  Clock, 
  MapPin, 
  Truck, 
  ChevronRight, 
  AlertCircle,
  Calendar,
  Search,
  X,
  CheckCircle,
  XCircle,
  ChevronLeft,
  Loader
} from "lucide-react";

// Stat Card Component
function StatCard({ title, value, icon: Icon, color, onClick }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-600",
    green: "bg-emerald-50 border-emerald-200 text-emerald-600",
    red: "bg-red-50 border-red-200 text-red-600",
  };
  
  const bgColor = colorClasses[color] || colorClasses.blue;
  
  return (
    <div
      onClick={onClick}
      className={`${bgColor} border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md`}
    >
      <div className="flex items-center justify-between mb-1">
        <Icon size={14} className={bgColor.split(' ')[2]} />
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs font-medium">{title}</p>
    </div>
  );
}

// Booking Card Component
function BookingCard({ booking, onSelect, getStatusColor, getStatusLabel, formatDateTime, getDuration }) {
  return (
    <div
      onClick={() => onSelect(booking)}
      className="bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden"
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900">
                Booking #{booking.bId}
              </h3>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                {getStatusLabel(booking.status)}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Requested by: {booking.bookingSno}
            </p>
          </div>
          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
        </div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-1.5">
            <Truck size={12} className="text-gray-500" />
            <div>
              <p className="text-[10px] text-gray-500">Vehicle</p>
              <p className="text-xs font-medium text-gray-900">{booking.vId || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={12} className="text-gray-500" />
            <div>
              <p className="text-[10px] text-gray-500">Scheduled</p>
              <p className="text-xs font-medium text-gray-900">{formatDateTime(booking.bsTime)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-emerald-600" />
            <div>
              <p className="text-[10px] text-gray-500">From</p>
              <p className="text-xs font-medium text-gray-900 truncate">{booking.From || "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-red-600" />
            <div>
              <p className="text-[10px] text-gray-500">To</p>
              <p className="text-xs font-medium text-gray-900 truncate">{booking.TO || "—"}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-[10px] text-gray-500">Duration</p>
          <p className="text-xs font-semibold text-gray-900">{getDuration(booking.bsTime, booking.beTime)}</p>
        </div>

        {booking.Remark && (
          <div className="bg-yellow-50 border-l-2 border-yellow-400 rounded p-2">
            <p className="text-[10px] text-gray-500">Notes</p>
            <p className="text-xs text-gray-700">{booking.Remark}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Booking Detail Modal
function BookingDetailModal({ booking, isOpen, onClose, onAccept, getStatusColor, getStatusLabel, formatDateTime, getDuration, canAccept, minsLeft }) {
  if (!isOpen || !booking) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-50 rounded-lg">
              <Calendar size={16} className="text-blue-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">Booking Details</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div>
            <p className="text-xs text-gray-500">Booking ID</p>
            <p className="text-lg font-semibold text-gray-900">#{booking.bId}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500">Status</p>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
              {getStatusLabel(booking.status)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Vehicle</p>
              <p className="text-sm font-medium text-gray-900">{booking.vId || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-sm font-medium text-gray-900">{getDuration(booking.bsTime, booking.beTime)}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500">Scheduled Time</p>
            <p className="text-sm font-medium text-gray-900">{formatDateTime(booking.bsTime)}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500">From</p>
            <p className="text-sm font-medium text-gray-900">{booking.From || "—"}</p>
          </div>

          <div>
            <p className="text-xs text-gray-500">To</p>
            <p className="text-sm font-medium text-gray-900">{booking.TO || "—"}</p>
          </div>

          {booking.Remark && (
            <div>
              <p className="text-xs text-gray-500">Notes</p>
              <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded-lg">{booking.Remark}</p>
            </div>
          )}

          {booking.Priority && (
            <div>
              <p className="text-xs text-gray-500">Priority</p>
              <p className="text-sm font-medium text-gray-900">{booking.Priority}</p>
            </div>
          )}

          <div className={`rounded-lg p-3 text-sm ${canAccept ? "bg-emerald-50 border border-emerald-200" : "bg-yellow-50 border border-yellow-200"}`}>
            <p className="text-gray-700 font-medium">
              {canAccept
                ? "Scheduled time reached. You can accept this trip now."
                : `Trip starts in ${minsLeft} min. Accept will unlock at scheduled time.`}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Close
          </button>
          <button
            onClick={onAccept}
            disabled={!canAccept}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition ${
              canAccept
                ? "bg-gray-900 text-white hover:bg-gray-800"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Accept Trip
          </button>
        </div>
      </div>
    </div>
  );
}

// Filter Button Component
function FilterButton({ label, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
        isActive
          ? "bg-gray-900 text-white"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      {label}
    </button>
  );
}

export default function DriverScheduledBookings() {
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const [scheduledBookings, setScheduledBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [assignedVehicleId, setAssignedVehicleId] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  const selectedBookingRef = useRef(null);
  const isModalOpenRef = useRef(false);

  const normalizeDriverStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    if (s === "p" || s === "pending") return "Pending";
    if (s === "co" || s === "completed") return "Completed";
    if (s === "c" || s === "cancelled" || s === "canceled" || s === "cancel") return "Cancel";
    if (s === "a" || s === "active" || s === "en" || s === "enroute" || s === "ip" || s === "inprogress") return "Active";
    return status || "";
  };

  useEffect(() => {
    const fetchScheduled = async () => {
      if (!authKey || !userDetails?.ServiceNo) {
        setLoading(false);
        return;
      }
      
      try {
        const driverServiceNo = String(userDetails.ServiceNo).trim();
        const vehicles = await getAllVehicles();

        const assignedVehicle = (vehicles || []).find(
          (v) => String(v?.driverSno || "").trim() === driverServiceNo
        );

        const vehicleId = assignedVehicle?.vehicleId ? String(assignedVehicle.vehicleId).trim() : "";
        setAssignedVehicleId(vehicleId);

        if (!vehicleId) {
          setScheduledBookings([]);
          setLoading(false);
          return;
        }

        const vehicleBookings = await getBookingDetailsByVehicleId();

        const scheduled = (vehicleBookings || []).filter((b) => {
          const sameDriver = String(b?.dSNo || "").trim() === driverServiceNo;
          const sameVehicle = String(b?.vId || "").trim() === vehicleId;
          const isScheduled = String(b?.BookingStatus || "").trim().toLowerCase() === "s" ||
                              String(b?.BookingStatus || "").trim().toLowerCase() === "shedule booking" ||
                              String(b?.BookingStatus || "").trim().toLowerCase() === "schedule booking";
          return sameDriver && sameVehicle && isScheduled;
        });

        setScheduledBookings(
          [...scheduled]
            .sort((a, b) => {
              const aId = parseInt(a?.bId || 0, 10);
              const bId = parseInt(b?.bId || 0, 10);
              if (!isNaN(aId) && !isNaN(bId)) return bId - aId;
              return String(b?.bId || "").localeCompare(String(a?.bId || ""));
            })
            .map((b) => ({
              ...b,
              status: normalizeDriverStatus(b.status),
            }))
        );
      } catch (error) {
        console.error("Error fetching bookings:", error);
        toast.error("Failed to load scheduled bookings");
      } finally {
        setLoading(false);
      }
    };
    
    fetchScheduled();
  }, [authKey, userDetails?.ServiceNo]);

  const filteredBookings = useMemo(() => {
    return scheduledBookings.filter((booking) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        booking.bId?.toString().includes(search) ||
        booking.vId?.toLowerCase().includes(search) ||
        booking.From?.toLowerCase().includes(search) ||
        booking.TO?.toLowerCase().includes(search) ||
        booking.Remark?.toLowerCase().includes(search);

      let matchesFilter = false;
      const status = normalizeDriverStatus(booking.status);
      
      if (filterStatus === "ALL") matchesFilter = true;
      else if (filterStatus === "PENDING") matchesFilter = status === "Pending";
      else if (filterStatus === "ACTIVE") matchesFilter = status === "Active";
      else if (filterStatus === "COMPLETED") matchesFilter = status === "Completed";
      else if (filterStatus === "CANCELLED") matchesFilter = status === "Cancel";
      
      return matchesSearch && matchesFilter;
    });
  }, [scheduledBookings, searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + itemsPerPage);

  const stats = useMemo(() => {
    return {
      total: scheduledBookings.length,
      pending: scheduledBookings.filter(b => normalizeDriverStatus(b.status) === "Pending").length,
      active: scheduledBookings.filter(b => normalizeDriverStatus(b.status) === "Active").length,
      completed: scheduledBookings.filter(b => normalizeDriverStatus(b.status) === "Completed").length,
      cancelled: scheduledBookings.filter(b => normalizeDriverStatus(b.status) === "Cancel").length,
    };
  }, [scheduledBookings]);

  const getStatusColor = (status) => {
    const s = normalizeDriverStatus(status);
    if (s === "Pending") return "bg-yellow-50 text-yellow-700 border-yellow-200";
    if (s === "Active") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (s === "Completed") return "bg-blue-50 text-blue-700 border-blue-200";
    if (s === "Cancel") return "bg-red-50 text-red-700 border-red-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  const getStatusLabel = (status) => {
    const s = normalizeDriverStatus(status);
    if (s === "Pending") return "Pending";
    if (s === "Active") return "Active";
    if (s === "Completed") return "Completed";
    if (s === "Cancel") return "Cancelled";
    return "Unknown";
  };

  const formatDateTime = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) +
             " at " +
             date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return dateStr;
    }
  };

  const getDuration = (startTime, endTime) => {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return "—";
      const mins = Math.round((end - start) / 60000);
      return mins > 0 ? `${mins} min` : "—";
    } catch {
      return "—";
    }
  };

  const getBookingStartMs = (booking) => {
    const source = booking?.bsTime || booking?.bookingDT;
    const ms = new Date(source || "").getTime();
    return Number.isNaN(ms) ? null : ms;
  };

  const hasBookingStarted = (booking, now = Date.now()) => {
    const startMs = getBookingStartMs(booking);
    if (startMs === null) return true;
    return now >= startMs;
  };

  const getMinutesUntilStart = (booking, now = Date.now()) => {
    const startMs = getBookingStartMs(booking);
    if (startMs === null) return 0;
    return Math.max(0, Math.ceil((startMs - now) / 60000));
  };

  useEffect(() => {
    if (!isModalOpen) return;
    const timer = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [isModalOpen]);

  const selectedBookingStarted = selectedBooking ? hasBookingStarted(selectedBooking, nowTick) : false;
  const selectedBookingMinsLeft = selectedBooking ? getMinutesUntilStart(selectedBooking, nowTick) : 0;
  const selectedBookingReadyByStatus = selectedBooking ? normalizeDriverStatus(selectedBooking.status) === "Active" : false;
  const canAcceptSelectedBooking = selectedBookingStarted || selectedBookingReadyByStatus;

  useEffect(() => {
    selectedBookingRef.current = selectedBooking;
  }, [selectedBooking]);

  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  // WebSocket connection
  useEffect(() => {
    if (!authKey || !userDetails?.ServiceNo || !assignedVehicleId) return;

    const driverServiceNo = String(userDetails.ServiceNo).trim();
    bookingWS.connect(authKey, driverServiceNo, assignedVehicleId);

    bookingWS.onNewBooking((message) => {
      if (!message) return;

      const messageVehicleId = String(message?.vId || "").trim();
      const messageDriverSno = String(message?.dSNo || "").trim();
      const isForCurrentDriver = messageDriverSno === driverServiceNo || messageVehicleId === String(assignedVehicleId);

      if (!isForCurrentDriver) return;

      if (message.type === "booking_cancelled" || message.action === "booking_cancelled") {
        setScheduledBookings((prev) => prev.filter((b) => String(b?.bId) !== String(message?.bId)));
        if (selectedBookingRef.current && String(selectedBookingRef.current?.bId) === String(message?.bId)) {
          setIsModalOpen(false);
          setSelectedBooking(null);
        }
        const cancelText = message.message || (message.cancelledBy === "admin"
          ? `Booking #${message?.bId} was cancelled by Admin.`
          : `Booking #${message?.bId} was cancelled.`);
        toast.dismiss();
        toast.error(cancelText, { autoClose: 5000 });
        return;
      }

      setScheduledBookings((prev) => {
        const existingIndex = prev.findIndex((b) => String(b?.bId) === String(message?.bId));
        if (existingIndex === -1) {
          const sameDriver = String(message?.dSNo || "").trim() === driverServiceNo;
          const sameVehicle = String(message?.vId || "").trim() === String(assignedVehicleId);
          if (!sameDriver || !sameVehicle) return prev;

          const next = [...prev, { ...message, status: normalizeDriverStatus(message.status) }].sort((a, b) => {
            const aTime = new Date(a?.bsTime || a?.bookingDT || 0).getTime();
            const bTime = new Date(b?.bsTime || b?.bookingDT || 0).getTime();
            return aTime - bTime;
          });

          setSelectedBooking({ ...message, status: normalizeDriverStatus(message.status) });
          if (!isModalOpenRef.current) setIsModalOpen(true);
          toast.info(`New scheduled booking #${message?.bId} received.`);
          return next;
        }

        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...message, status: normalizeDriverStatus(message.status) };
        return updated;
      });

      if (selectedBookingRef.current && String(selectedBookingRef.current?.bId) === String(message?.bId)) {
        setSelectedBooking((prev) => (prev ? { ...prev, ...message, status: normalizeDriverStatus(message.status) } : prev));
      }
    });

    const unsubscribeStatus = bookingWS.onStatusUpdate((message) => {
      if (!message?.bId) return;

      setScheduledBookings((prev) => {
        let changed = false;
        const next = prev.map((b) => {
          if (String(b?.bId) !== String(message?.bId)) return b;
          changed = true;
          return { ...b, ...message, status: normalizeDriverStatus(message.status) };
        });
        return changed ? next : prev;
      });

      if (selectedBookingRef.current && String(selectedBookingRef.current?.bId) === String(message?.bId)) {
        setSelectedBooking((prev) => (prev ? { ...prev, ...message, status: normalizeDriverStatus(message.status) } : prev));
      }
    });

    return () => {
      bookingWS.onNewBooking(null);
      if (typeof unsubscribeStatus === "function") unsubscribeStatus();
      bookingWS.disconnect();
    };
  }, [authKey, userDetails?.ServiceNo, assignedVehicleId]);

  const handleAcceptBooking = () => {
    if (!canAcceptSelectedBooking) {
      toast.warn("You can accept this trip only at scheduled time.");
      return;
    }
    toast.success("Trip accepted successfully.");
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center py-12">
          <Loader size={24} className="text-gray-400 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading scheduled bookings...</span>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      {/* Header Section */}
      <div className="mb-4">
        <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Scheduled Bookings</h1>
        <p className="text-xs text-gray-500 mt-0.5">View and manage your upcoming scheduled trips</p>
        {assignedVehicleId && (
          <p className="text-xs text-gray-500 mt-1">
            Assigned Vehicle: <span className="font-medium text-gray-700">{assignedVehicleId}</span>
          </p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <StatCard title="Total" value={stats.total} icon={Calendar} color="blue" />
        <StatCard title="Pending" value={stats.pending} icon={Clock} color="yellow" />
        <StatCard title="Active" value={stats.active} icon={CheckCircle} color="green" />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="blue" />
        <StatCard title="Cancelled" value={stats.cancelled} icon={XCircle} color="red" />
      </div>

      {/* Search & Filter */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID, vehicle, location, or notes..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2"
            >
              <X size={12} className="text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        
        <div className="flex flex-wrap gap-1.5 mt-3">
          {["ALL", "PENDING", "ACTIVE", "COMPLETED", "CANCELLED"].map((status) => (
            <FilterButton
              key={status}
              label={status}
              isActive={filterStatus === status}
              onClick={() => {
                setFilterStatus(status);
                setCurrentPage(1);
              }}
            />
          ))}
        </div>
      </div>

      {/* Bookings List */}
      {paginatedBookings.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-12 text-center">
          <div className="flex flex-col items-center gap-2">
            <Calendar size={32} className="text-gray-300" />
            <p className="text-sm font-medium text-gray-900">No Scheduled Bookings</p>
            <p className="text-xs text-gray-500">
              {filteredBookings.length === 0 && scheduledBookings.length > 0
                ? "No bookings match your filters. Try adjusting your search."
                : "You don't have any scheduled bookings yet."}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {paginatedBookings.map((booking) => (
              <BookingCard
                key={booking.bId}
                booking={booking}
                onSelect={(booking) => {
                  setSelectedBooking(booking);
                  setIsModalOpen(true);
                }}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
                formatDateTime={formatDateTime}
                getDuration={getDuration}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredBookings.length)} of {filteredBookings.length}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-2 py-1 text-xs text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Booking Detail Modal */}
      <BookingDetailModal
        booking={selectedBooking}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAccept={handleAcceptBooking}
        getStatusColor={getStatusColor}
        getStatusLabel={getStatusLabel}
        formatDateTime={formatDateTime}
        getDuration={getDuration}
        canAccept={canAcceptSelectedBooking}
        minsLeft={selectedBookingMinsLeft}
      />
    </DriverLayout>
  );
}